import { Component, computed, effect, inject, Signal } from '@angular/core';
import { FormArray, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { SectionComponent } from '../../../../components/layout/section/section.component';
import {
  EPage,
  DirectTicketConfigurationShoppingCartComponent,
} from '../../../../components/feedback/direct-ticket-configuration-shopping-cart/direct-ticket-configuration-shopping-cart.component';
import { CorporateDesignService } from '../../../../services/corporate-design/corporate-design.service';
import { Router } from '@angular/router';
import { ConfigurationStore } from '../../../../services/api/configuration/configuration-store.service';
import {
  CONFIGURATION_SETTING_KEYS,
  EEntityType,
  EPaymentProviders,
  ERelationType,
  IConfiguration,
  ICorporateDesign,
  TariffProductDefinition,
  TariffProductGroup,
  UserDto,
} from '@web/dtos';
import { ITicketPersonalDataForm } from '../direct-ticket-personal-data-page/direct-ticket-personal-data-page.component';
import { ShoppingCartService } from '../../../../services/shopping-cart/shopping-cart.service';
import { ITicketDefinitionForm } from '../../../../services/shopping-cart/shopping-cart-form.service';
import { LoadingAnimationComponent } from '../../../../components/feedback/loading-animation/loading-animation.component';
import { SectionHeadlineComponent } from '../../../../components/layout/section/section-header/section-headline/section-headline.component';
import { UserStore } from '../../../../services/api/user/user/user-store.service';
import { PaymentPreferenceStore } from '../../../../services/api/user/paymentPreference/payment-preference-store.service';
import { FilterTextsPipe } from '../../../../pipes/filterTexts/filterTexts.pipe';
import { LanguagePipe } from '../../../../pipes/language/language.pipe';
import { WebTextStore } from '../../../../services/api/text/web-text-store.service';
import { ERoute } from '../../../../constants/routes.enum';
import { AuthProviderFactory } from '../../../../services/auth/auth-provider.factory';
import { AuthProvider } from '../../../../services/auth/auth.provider';

@Component({
  selector: 'aw-direct-ticket-checkout-page',
  imports: [
    ReactiveFormsModule,
    SectionComponent,
    DirectTicketConfigurationShoppingCartComponent,
    LoadingAnimationComponent,
    SectionHeadlineComponent,
    FilterTextsPipe,
    LanguagePipe,
  ],
  templateUrl: './direct-ticket-checkout-page.component.html',
})
export class DirectTicketCheckoutPageComponent {
  protected readonly corporateDesignService: CorporateDesignService = inject(
    CorporateDesignService
  );
  private readonly router: Router = inject(Router);
  protected readonly configurationStore: ConfigurationStore =
    inject(ConfigurationStore);
  protected readonly paymentPreferenceStore: PaymentPreferenceStore = inject(
    PaymentPreferenceStore
  );
  protected readonly userStore: UserStore = inject(UserStore);
  protected readonly shoppingCartService: ShoppingCartService =
    inject(ShoppingCartService);
  protected readonly textStore: WebTextStore = inject(WebTextStore);

  protected readonly EPage: typeof EPage = EPage;
  protected readonly ERelationType: typeof ERelationType = ERelationType;
  protected readonly EEntityType: typeof EEntityType = EEntityType;

  private readonly auth: AuthProvider =
    inject(AuthProviderFactory).getProvider('shop');

  protected readonly corporateDesign: Signal<ICorporateDesign> =
    this.corporateDesignService.corporateDesign;
  protected readonly configuration: Signal<IConfiguration[]> =
    this.configurationStore.configuration;
  protected readonly ticketConfiguration: Signal<
    TariffProductGroup | undefined
  > = this.shoppingCartService.directTicketConfiguration;
  protected readonly currentProduct: Signal<
    TariffProductDefinition | undefined
  > = this.shoppingCartService.currentProductDefinition;
  protected readonly isLoading: Signal<boolean> =
    this.shoppingCartService.isLoadingTicketConfiguration;

  protected readonly productForms: FormArray<FormGroup<ITicketDefinitionForm>> =
    this.shoppingCartService.productForms;
  protected readonly personalDataForm: FormGroup<ITicketPersonalDataForm> =
    this.shoppingCartService.personalDataForm;
  protected readonly userDto: Signal<UserDto | undefined> = computed(() => {
    if (this.auth.isLoggedIn() && this.userStore.user()) {
      return this.userStore.user();
    }
    return undefined;
  });

  constructor() {
    effect(() => {
      const userId: string | undefined = this.userStore.user()?.userId;
      if (userId) {
        this.paymentPreferenceStore.loadPreference(userId);
      }
    });
  }

  protected proceedToPayment(): void {
    this.router.navigate([
      `/${ERoute.directTicketConfiguration}/${ERoute.payment}/`,
    ]);
  }

  protected readonly CONFIGURATION_SETTING_KEYS: typeof CONFIGURATION_SETTING_KEYS =
    CONFIGURATION_SETTING_KEYS;
  protected readonly EPaymentProviders: typeof EPaymentProviders =
    EPaymentProviders;
}
